import type { RouteSegment } from '@/api/main/types.gen'
import { REMOVAL_GAP_SEGMENT_ID } from './constants'
import { findShortestPath, type RoutingSource } from './shortestPath'
import type { PendingRemoval } from './types'

export function findLoopForIndex(pathNodes: string[], k: number): { loopStart: number; loopEnd: number } | null {
  let bestStart = -1
  let bestEnd = pathNodes.length
  let bestSize = Infinity

  for (let a = 0; a <= k; a++) {
    for (let b = k; b < pathNodes.length; b++) {
      if (a < b && pathNodes[a] === pathNodes[b]) {
        const size = b - a
        if (size < bestSize || (size === bestSize && a > bestStart)) {
          bestStart = a
          bestEnd = b
          bestSize = size
        }
      }
    }
  }

  if (bestStart === -1 || bestEnd - bestStart > 10) return null
  return { loopStart: bestStart, loopEnd: bestEnd }
}

/** Single visit → pending range (expands small loops like legacy). */
export function removalFromVisitIndex(pathNodes: string[], visitIndex: number): PendingRemoval {
  const loop = findLoopForIndex(pathNodes, visitIndex)
  if (loop) {
    return { kind: 'range', rangeStart: loop.loopStart + 1, rangeEnd: loop.loopEnd - 1 }
  }
  return { kind: 'range', rangeStart: visitIndex, rangeEnd: visitIndex }
}

/**
 * First pick sets the start; further picks expand the inclusive span.
 * Always includes the raw visit index so multipass same-node visits become a range.
 */
export function mergeRemovalWithVisit(
  prev: PendingRemoval | null,
  pathNodes: string[],
  visitIndex: number
): PendingRemoval {
  const next = removalFromVisitIndex(pathNodes, visitIndex)
  if (!prev || prev.kind !== 'range') return next
  return {
    kind: 'range',
    rangeStart: Math.min(prev.rangeStart, next.rangeStart, visitIndex),
    rangeEnd: Math.max(prev.rangeEnd, next.rangeEnd, visitIndex),
  }
}

export type RemovalPlan =
  | { status: 'ready'; replacement: RouteSegment[]; mode: 'reconnect' | 'truncate' | 'gap' }
  | { status: 'invalid' }

function gapReplacement(fromNode: string, toNode: string): RouteSegment[] {
  if (fromNode === toNode) return []
  return [
    {
      street_segment_id: REMOVAL_GAP_SEGMENT_ID,
      entrance_node_id: fromNode,
      exit_node_id: toNode,
    },
  ]
}

/**
 * Middle removals reconnect via any legal bypass (length unrestricted).
 * When no bypass exists, removal is still ready and leaves a gap marker (or empty splice if same node).
 * Suffix/prefix truncations do not need a reconnect.
 */
export function computeRemovalPlan(
  pending: PendingRemoval,
  pathNodes: string[],
  streetSegments: RoutingSource
): RemovalPlan {
  const { rangeStart, rangeEnd } = pending
  const lastIdx = pathNodes.length - 1
  if (lastIdx < 0 || rangeStart < 0 || rangeEnd < rangeStart || rangeEnd > lastIdx) {
    return { status: 'invalid' }
  }

  if (rangeEnd === lastIdx) {
    return { status: 'ready', replacement: [], mode: 'truncate' }
  }

  if (rangeStart === 0) {
    return { status: 'ready', replacement: [], mode: 'truncate' }
  }

  const fromNode = pathNodes[rangeStart - 1]
  const toNode = pathNodes[rangeEnd + 1]
  if (!fromNode || !toNode) return { status: 'invalid' }

  const avoidNodes = new Set(pathNodes.slice(rangeStart, rangeEnd + 1).filter(Boolean) as string[])
  const bypass = findShortestPath(streetSegments, fromNode, toNode, avoidNodes)
  if (bypass) {
    return { status: 'ready', replacement: bypass, mode: 'reconnect' }
  }

  return { status: 'ready', replacement: gapReplacement(fromNode, toNode), mode: 'gap' }
}

/** Argument for `removeLoop(loopStart, loopEnd, replacement)`. */
export function removalLoopBounds(pending: PendingRemoval): { loopStart: number; loopEnd: number } {
  return {
    loopStart: pending.rangeStart === 0 ? 0 : pending.rangeStart - 1,
    loopEnd: pending.rangeEnd + 1,
  }
}
